import {
    faBan,
    faCircleInfo,
    faFileExport,
    faMoneyBill,
    faPaperPlane,
    faPencil,
    faTrashAlt
} from '@fortawesome/pro-light-svg-icons';
import {
    getInvoiceThunk,
    InvoicesStatus,
    saveDateThunk,
    voidInvoicesThunk,
    deleteInvoicesThunk,
    updatePdfThunk,
    issueInvoiceThunk
} from '@spike/invoices-action';
import Invoice, { InvoiceLine, InvoiceListRecord } from '@spike/invoice-model';
import { Payment } from '@spike/payments-model';
import { showSuccess } from '@spike/notifications-action';
import { PaymentsStatus, sendThunk } from '@spike/payments-action';
import clsx from 'clsx';
import InvoiceResumeTaxes from 'components/Checkout2/InvoiceResumeTaxes';
import Transactions from 'components/Checkout2/SplitPayments/Transactions';
import ResumeLine from 'components/Checkout2/UI/ResumeLine';
import TotalResumeLine from 'components/Checkout2/UI/TotalResumeLine';
import { formatAmount } from 'components/Checkout2/utils/OrderUtils';
import { ToolbarButton, ToolbarItem } from 'components/UI';
import Receipt from 'components/UI/Receipt/Receipt';
import Button from 'components/UI/V3/Button/Button';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { DrawerFooter } from 'components/UI/V3/Drawer/Footer/DrawerFooter';
import AlertMessage from 'components/UI/V3/AlertMessage';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import TabsHeader from 'components/UI/TabsHeader/TabsHeader';
import useNonInitialEffect from '@versiondos/hooks';
import { useApiClientWrapper, usePermissions, useRoles } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Moment } from 'moment-timezone';
import { RootState } from 'store';
import InvoiceStatus from '../InvoiceStatus';
import DeleteInvoiceModal from '../Delete/DeleteInvoiceModal';
import EditInvoiceDateModal from '../Edit/EditInvoiceDateModal';
import SendInvoiceModal from '../Send/SendInvoiceModal';
import VoidInvoiceModal from '../Void/VoidInvoiceModal';
import IssueInvoiceModal from '../Issue/IssueInvoiceModal';
import styles from './InvoiceDetail.module.scss';
import {
    getLineName,
    getLineTypeLabel,
    getOriginalLineTotal,
    getTotalDiscount,
    shouldShowCheckoutOption,
    isInvoiceDraft,
    isInvoiceVoided,
    toInvoiceListRecordForStatus
} from './invoiceUtils';

const detailsTabId = 'details';
const transactionsTabId = 'transactions';
const defaultDrawerZIndex = 10002;

interface InvoiceDetailProps {
    invoiceId: number;
    marketplaceId: number;
    marketplaceName?: string;
    className?: string;
    zIndex?: number;
    isCheckoutDisabled?: boolean;
    onClose: () => void;
    onCheckout?: (bookingId: number | undefined, orderId: number | undefined, invoiceUuid: string) => void;
}

interface InvoiceDetailToolbarProps {
    invoice: Invoice;
    marketplaceId: number;
    zIndex: number;
    isCheckoutDisabled?: boolean;
    onCheckout: () => void;
    onSendInvoice: () => void;
    onVoidInvoice: () => void;
    onDeleteInvoice: () => void;
    onEditDate: () => void;
    onIssueInvoice: () => void;
}

const InvoiceDetailToolbar: FunctionComponent<InvoiceDetailToolbarProps> = ({
    invoice,
    marketplaceId,
    zIndex,
    isCheckoutDisabled,
    onCheckout,
    onSendInvoice,
    onVoidInvoice,
    onDeleteInvoice,
    onEditDate,
    onIssueInvoice
}) => {
    const permissions = usePermissions();
    const { isOwner } = useRoles();
    const { isUserLoggedInMarketplace } = useOrganizationMarketplaces();

    if (!isUserLoggedInMarketplace(marketplaceId)) {
        return null;
    }

    const toolbarOptions = (
        isInvoiceDraft(invoice)
            ? [
                  <ToolbarItem
                      key="issue-invoice"
                      text="Issue Invoice Now"
                      icon={faFileExport}
                      isDisabled={isCheckoutDisabled}
                      onClick={event => {
                          event.stopPropagation();
                          onIssueInvoice();
                      }}
                  />,
                  <ToolbarItem
                      key="delete-draft"
                      text="Delete Draft"
                      icon={faTrashAlt}
                      color="#EF4F57"
                      isDisabled={isCheckoutDisabled}
                      onClick={event => {
                          event.stopPropagation();
                          onDeleteInvoice();
                      }}
                  />
              ]
            : [
                  permissions.checkout.takePayments && shouldShowCheckoutOption(invoice) ? (
                      <ToolbarItem
                          key="checkout"
                          text="Checkout"
                          icon={faMoneyBill}
                          isDisabled={isCheckoutDisabled}
                          onClick={event => {
                              event.stopPropagation();
                              onCheckout();
                          }}
                      />
                  ) : null,
                  !isInvoiceVoided(invoice) ? (
                      <ToolbarItem
                          key="send-invoice"
                          text="Send Invoice"
                          icon={faPaperPlane}
                          isDisabled={isCheckoutDisabled}
                          onClick={event => {
                              event.stopPropagation();
                              onSendInvoice();
                          }}
                      />
                  ) : null,
                  isOwner ? (
                      <ToolbarItem
                          key="edit-date"
                          text="Edit Date"
                          icon={faPencil}
                          isDisabled={isCheckoutDisabled}
                          onClick={event => {
                              event.stopPropagation();
                              onEditDate();
                          }}
                      />
                  ) : null,
                  invoice.voidable ? (
                      <ToolbarItem
                          key="void"
                          text="Void"
                          icon={faBan}
                          color="#EF4F57"
                          isDisabled={isCheckoutDisabled}
                          onClick={event => {
                              event.stopPropagation();
                              onVoidInvoice();
                          }}
                      />
                  ) : null,
                  isOwner && invoice.destroyable ? (
                      <ToolbarItem
                          key="delete"
                          text="Delete"
                          icon={faTrashAlt}
                          color="#EF4F57"
                          isDisabled={isCheckoutDisabled}
                          onClick={event => {
                              event.stopPropagation();
                              onDeleteInvoice();
                          }}
                      />
                  ) : null
              ]
    ).filter(Boolean);

    if (toolbarOptions.length === 0) {
        return null;
    }

    return (
        <ToolbarButton
            className={styles.toolbarButton}
            disabled={isCheckoutDisabled}
            zIndex={zIndex}
        >
            {toolbarOptions}
        </ToolbarButton>
    );
};

const getDiscountLabel = (invoice: Invoice, totalDiscount: number): string => {
    const grossTotal = Number(invoice.grossTotal);

    if (grossTotal > 0 && totalDiscount > 0) {
        const percentage = Math.round((totalDiscount / grossTotal) * 100);
        return `Discount (${percentage}%)`;
    }

    return 'Discount';
};

interface InvoiceLineItemProps {
    line: InvoiceLine;
}

const InvoiceLineItem: FunctionComponent<InvoiceLineItemProps> = ({ line }) => {
    const originalTotal = getOriginalLineTotal(line);

    return (
        <div className={styles.item}>
            <div className={styles.itemInfo}>
                <span className={styles.itemName}>{getLineName(line)}</span>
                <span className={styles.itemType}>{getLineTypeLabel(line)}</span>
            </div>
            <div className={styles.itemPriceGroup}>
                <div className={styles.itemPriceRow}>
                    {line.quantity > 1 && (
                        <>
                            <span className={styles.itemQuantity}>{line.quantity}</span>
                            <span className={styles.itemQuantityX}>x</span>
                        </>
                    )}
                    <span className={styles.itemPrice}>{formatAmount(line.total)}</span>
                </div>
                {originalTotal !== null && (
                    <span className={styles.itemOriginalPrice}>{formatAmount(originalTotal.toFixed(2))}</span>
                )}
            </div>
        </div>
    );
};

interface InvoiceDetailContentProps {
    invoice: Invoice;
    marketplaceId: number;
    marketplaceName?: string;
    toolbarZIndex: number;
    isCheckoutDisabled?: boolean;
    onCheckout: () => void;
    onSendInvoice: () => void;
    onVoidInvoice: () => void;
    onDeleteInvoice: () => void;
    onEditDate: () => void;
    onIssueInvoice: () => void;
}

const InvoiceDetailContent: FunctionComponent<InvoiceDetailContentProps> = ({
    invoice,
    marketplaceId,
    marketplaceName,
    toolbarZIndex,
    isCheckoutDisabled,
    onCheckout,
    onSendInvoice,
    onVoidInvoice,
    onDeleteInvoice,
    onEditDate,
    onIssueInvoice
}) => {
    const totalDiscount = getTotalDiscount(invoice);
    const paymentsReceived = Number(invoice.payments.totalWithoutDeposits);
    const paidDepositsTotal = invoice.deposits.list.reduce((total, deposit) => {
        if (!deposit.paid) {
            return total;
        }

        return total + Number(deposit.amount);
    }, 0);
    const refundsIssued = Number(invoice.totalRefunds);
    const showNoChargeableItemsMessage =
        invoice.noChargeableItems.hasNoChargeableItems && invoice.noChargeableItems.message.body !== '';
    const showItemsTitle = !showNoChargeableItemsMessage || invoice.lines.length > 0;

    return (
        <>
            <div className={styles.header}>
                <div className={styles.titleRow}>
                    <div className={styles.titleGroup}>
                        <h2 className={styles.title}>Invoice {invoice.number}</h2>
                        <InvoiceStatus
                            invoiceStatus={invoice.status}
                            hasRefunds={Number(invoice.totalRefunds) > 0}
                        />
                    </div>
                    <InvoiceDetailToolbar
                        invoice={invoice}
                        marketplaceId={marketplaceId}
                        zIndex={toolbarZIndex}
                        isCheckoutDisabled={isCheckoutDisabled}
                        onCheckout={onCheckout}
                        onSendInvoice={onSendInvoice}
                        onVoidInvoice={onVoidInvoice}
                        onDeleteInvoice={onDeleteInvoice}
                        onEditDate={onEditDate}
                        onIssueInvoice={onIssueInvoice}
                    />
                </div>
                <p className={styles.amount}>{formatAmount(invoice.grandTotal)}</p>
            </div>

            <section className={styles.section}>
                <div className={styles.infoRow}>
                    <span className={styles.fieldLabel}>Client</span>
                    <span className={styles.fieldValue}>{invoice.customer?.name ?? ''}</span>
                </div>
                <div className={styles.infoRow}>
                    <span className={styles.fieldLabel}>Invoice Date</span>
                    <span className={styles.fieldValue}>{invoice.invoiceDate.format('MM/DD/YYYY, h:mm a')}</span>
                </div>
                {marketplaceName && (
                    <div className={styles.infoRow}>
                        <span className={styles.fieldLabel}>Location</span>
                        <span className={styles.fieldValue}>{marketplaceName}</span>
                    </div>
                )}
            </section>

            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Summary</h3>
                <ResumeLine
                    description="Subtotal"
                    value={formatAmount(invoice.grossTotal)}
                    className={styles.summaryRow}
                    descriptionClassName={styles.summaryFieldLabel}
                    valueClassName={styles.fieldValue}
                />
                {totalDiscount > 0 && (
                    <ResumeLine
                        description={getDiscountLabel(invoice, totalDiscount)}
                        value={formatAmount(`-${totalDiscount.toFixed(2)}`)}
                        className={styles.summaryRow}
                        descriptionClassName={styles.summaryFieldLabel}
                        valueClassName={styles.fieldValue}
                    />
                )}
                {Number(invoice.tax.amount) > 0 && (
                    <InvoiceResumeTaxes
                        taxes={invoice.tax}
                        defaultExpanded
                        lineClassName={styles.summaryRow}
                        detailClassName={styles.taxDetail}
                        descriptionClassName={styles.summaryFieldLabel}
                        valueClassName={styles.fieldValue}
                    />
                )}
                {Number(invoice.surcharge) > 0 && (
                    <ResumeLine
                        description="Surcharge"
                        value={formatAmount(invoice.surcharge)}
                        className={styles.summaryRow}
                        descriptionClassName={styles.summaryFieldLabel}
                        valueClassName={styles.fieldValue}
                    />
                )}
                {Number(invoice.tip?.amount ?? 0) > 0 && (
                    <ResumeLine
                        description="Tip"
                        value={formatAmount(invoice.tip?.amount ?? undefined)}
                        className={styles.summaryRow}
                        descriptionClassName={styles.summaryFieldLabel}
                        valueClassName={styles.fieldValue}
                    />
                )}
                <div className={styles.summaryDivider} />
                <TotalResumeLine
                    description="Total"
                    value={formatAmount(invoice.grandTotal)}
                    className={styles.summaryTotalRow}
                    descriptionClassName={styles.totalRowText}
                    valueClassName={styles.totalRowValue}
                />
                {paidDepositsTotal > 0 && (
                    <ResumeLine
                        description="Deposits"
                        value={formatAmount(`-${paidDepositsTotal.toFixed(2)}`)}
                        className={styles.summaryRow}
                        descriptionClassName={styles.summaryFieldLabel}
                        valueClassName={styles.fieldValue}
                    />
                )}
                {paymentsReceived > 0 && (
                    <ResumeLine
                        description="Payments Received"
                        value={formatAmount(`-${paymentsReceived.toFixed(2)}`)}
                        className={styles.summaryRow}
                        descriptionClassName={styles.summaryFieldLabel}
                        valueClassName={styles.fieldValue}
                    />
                )}
                {refundsIssued > 0 && (
                    <ResumeLine
                        description="Refunds Issued"
                        value={formatAmount(invoice.totalRefunds)}
                        className={styles.summaryRow}
                        descriptionClassName={styles.summaryFieldLabel}
                        valueClassName={styles.fieldValue}
                    />
                )}
                <TotalResumeLine
                    description="Balance Due"
                    value={formatAmount(invoice.balance)}
                    descriptionClassName={styles.totalRowText}
                    valueClassName={styles.totalRowValue}
                />
            </section>

            <section className={styles.section}>
                {showNoChargeableItemsMessage && (
                    <AlertMessage
                        icon={faCircleInfo}
                        iconColor="#BAA997"
                        bdColor="#BCB8AE"
                        bgColor="#F8F5F1"
                        className={styles.noChargeableItemsAlert}
                    >
                        <div className={styles.noChargeableItemsAlertContent}>
                            <p className={styles.noChargeableItemsAlertTitle}>
                                {invoice.noChargeableItems.message.title}
                            </p>
                            <p className={styles.noChargeableItemsAlertBody}>
                                {invoice.noChargeableItems.message.body}
                            </p>
                        </div>
                    </AlertMessage>
                )}
                {showItemsTitle && <h3 className={styles.sectionTitle}>Items</h3>}
                {invoice.lines.map(line => (
                    <InvoiceLineItem
                        key={line.uuid}
                        line={line}
                    />
                ))}
            </section>
        </>
    );
};

export const InvoiceDetail: FunctionComponent<InvoiceDetailProps> = ({
    invoiceId,
    marketplaceId,
    marketplaceName,
    className,
    zIndex,
    isCheckoutDisabled,
    onClose,
    onCheckout
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const permissions = usePermissions();
    const { isUserLoggedInMarketplace } = useOrganizationMarketplaces();
    const [selectedTabId, setSelectedTabId] = useState(detailsTabId);
    const [showReceipt, setShowReceipt] = useState<Payment | undefined>(undefined);
    const [invoiceToSend, setInvoiceToSend] = useState<InvoiceListRecord | null>(null);
    const [invoiceToEditDate, setInvoiceToEditDate] = useState<InvoiceListRecord | null>(null);
    const [invoiceToVoid, setInvoiceToVoid] = useState<InvoiceListRecord | null>(null);
    const [invoiceToDelete, setInvoiceToDelete] = useState<InvoiceListRecord | null>(null);
    const [invoiceToIssue, setInvoiceToIssue] = useState<InvoiceListRecord | null>(null);
    const [sendingInvoice, setSendingInvoice] = useState(false);
    const pendingVoidRef = useRef(false);
    const pendingDeleteRef = useRef(false);
    const pendingIssueRef = useRef(false);
    const pendingSendRef = useRef(false);
    const pendingDateSaveRef = useRef(false);
    const pendingPdfRef = useRef(false);

    const invoice = useSelector<RootState, Invoice | undefined>(state => state.invoices.invoice);
    const invoicesStatus = useSelector<RootState, InvoicesStatus>(state => state.invoices.status);
    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);

    const invoiceListRecord = useMemo(
        () => (invoice?.id === invoiceId ? toInvoiceListRecordForStatus(invoice, marketplaceName) : null),
        [invoice, invoiceId, marketplaceName]
    );

    const voidingInvoice = invoicesStatus === InvoicesStatus.VoidingInvoice;
    const deletingInvoice = invoicesStatus === InvoicesStatus.DeletingInvoice;
    const issuingInvoice = invoicesStatus === InvoicesStatus.IssuingInvoice;
    const savingInvoiceDate = invoicesStatus === InvoicesStatus.SavingDate;
    const updatingPdf = invoicesStatus === InvoicesStatus.UpdatingPdf;

    useEffect(() => {
        dispatch(getInvoiceThunk(apiClientWrapper, invoiceId));
    }, [dispatch, invoiceId]);

    const isLoading = useMemo(() => {
        if (invoicesStatus === InvoicesStatus.Error) {
            return false;
        }

        return invoicesStatus === InvoicesStatus.GettingInvoice || invoice?.id !== invoiceId;
    }, [invoice?.id, invoiceId, invoicesStatus]);

    useNonInitialEffect(() => {
        switch (invoicesStatus) {
            case InvoicesStatus.SaveDateSuccess:
                if (pendingDateSaveRef.current) {
                    pendingDateSaveRef.current = false;
                    setInvoiceToEditDate(null);
                    dispatch(showSuccess('Invoice date updated.'));
                    dispatch(getInvoiceThunk(apiClientWrapper, invoiceId));
                }
                break;
            case InvoicesStatus.VoidInvoiceSuccess:
                if (pendingVoidRef.current) {
                    pendingVoidRef.current = false;
                    setInvoiceToVoid(null);
                    dispatch(showSuccess('Invoice voided.'));
                    onClose();
                }
                break;
            case InvoicesStatus.DeleteInvoiceSuccess:
                if (pendingDeleteRef.current) {
                    pendingDeleteRef.current = false;
                    setInvoiceToDelete(null);
                    dispatch(showSuccess('Invoice deleted.'));
                    onClose();
                }
                break;
            case InvoicesStatus.IssuedInvoiceSuccess:
                if (pendingIssueRef.current) {
                    pendingIssueRef.current = false;
                    setInvoiceToIssue(null);
                    dispatch(showSuccess('Invoice issued.'));
                    dispatch(getInvoiceThunk(apiClientWrapper, invoiceId));
                }
                break;
            case InvoicesStatus.UpdatePdfSuccess:
                if (pendingPdfRef.current) {
                    pendingPdfRef.current = false;
                    openPdfHandler();
                }
                break;
            case InvoicesStatus.Error:
                if (pendingDateSaveRef.current) {
                    pendingDateSaveRef.current = false;
                }
                if (pendingVoidRef.current) {
                    pendingVoidRef.current = false;
                }
                if (pendingDeleteRef.current) {
                    pendingDeleteRef.current = false;
                }
                if (pendingIssueRef.current) {
                    pendingIssueRef.current = false;
                }
                if (pendingPdfRef.current) {
                    pendingPdfRef.current = false;
                }
                break;
        }
    }, [invoicesStatus, dispatch, onClose, apiClientWrapper, invoiceId]);

    useNonInitialEffect(() => {
        if (PaymentsStatus.SendSuccess === paymentsStatus) {
            if (pendingSendRef.current) {
                pendingSendRef.current = false;
                setSendingInvoice(false);
                setInvoiceToSend(null);
            }
        } else if (PaymentsStatus.Error === paymentsStatus && pendingSendRef.current) {
            pendingSendRef.current = false;
            setSendingInvoice(false);
        }
    }, [paymentsStatus]);

    const showCheckoutOption =
        invoice?.id === invoiceId &&
        permissions.checkout.takePayments &&
        shouldShowCheckoutOption(invoice) &&
        isUserLoggedInMarketplace(marketplaceId);

    const tabs = useMemo(
        () => [
            { id: detailsTabId, name: 'Details' },
            { id: transactionsTabId, name: 'Transactions' }
        ],
        []
    );

    const selectTabHandler = (tabId: string) => {
        setShowReceipt(undefined);
        setSelectedTabId(tabId);
    };

    const refundedHandler = () => {
        dispatch(getInvoiceThunk(apiClientWrapper, invoiceId));
        setShowReceipt(undefined);
    };

    const startCheckoutHandler = () => {
        if (isCheckoutDisabled || !onCheckout || !invoice) {
            return;
        }

        onCheckout(invoice.bookingId, invoice.orderId ?? undefined, invoice.uuid);
    };

    const viewPdfHandler = () => {
        if (!invoice?.id) {
            return;
        }

        pendingPdfRef.current = true;
        dispatch(updatePdfThunk(apiClientWrapper, invoice.id));
    };

    const openPdfHandler = () => {
        if (!invoice?.pdfUrl) {
            return;
        }

        window.open(invoice.pdfUrl, '_blank', 'noopener,noreferrer');
    };

    const openSendInvoiceHandler = () => {
        if (invoiceListRecord) {
            setInvoiceToSend(invoiceListRecord);
        }
    };

    const closeSendInvoiceHandler = () => {
        if (!sendingInvoice) {
            setInvoiceToSend(null);
        }
    };

    const sendInvoiceHandler = useCallback(
        (email: string) => {
            if (!invoiceToSend) {
                return;
            }

            setSendingInvoice(true);
            pendingSendRef.current = true;
            dispatch(sendThunk(apiClientWrapper, invoiceToSend.id, true, false, false, email));
        },
        [apiClientWrapper, dispatch, invoiceToSend]
    );

    const openVoidInvoiceHandler = () => {
        if (invoiceListRecord) {
            setInvoiceToVoid(invoiceListRecord);
        }
    };

    const closeVoidInvoiceHandler = () => {
        if (!voidingInvoice) {
            setInvoiceToVoid(null);
        }
    };

    const voidInvoiceHandler = useCallback(() => {
        if (!invoiceToVoid) {
            return;
        }

        pendingVoidRef.current = true;
        dispatch(voidInvoicesThunk(apiClientWrapper, invoiceToVoid.id));
    }, [apiClientWrapper, dispatch, invoiceToVoid]);

    const openDeleteInvoiceHandler = () => {
        if (invoiceListRecord) {
            setInvoiceToDelete(invoiceListRecord);
        }
    };

    const closeDeleteInvoiceHandler = () => {
        if (!deletingInvoice) {
            setInvoiceToDelete(null);
        }
    };

    const deleteInvoiceHandler = useCallback(() => {
        if (!invoiceToDelete) {
            return;
        }

        pendingDeleteRef.current = true;
        dispatch(deleteInvoicesThunk(apiClientWrapper, invoiceToDelete.id));
    }, [apiClientWrapper, dispatch, invoiceToDelete]);

    const openIssueInvoiceHandler = () => {
        if (invoiceListRecord) {
            setInvoiceToIssue(invoiceListRecord);
        }
    };

    const closeIssueInvoiceHandler = () => {
        if (!issuingInvoice) {
            setInvoiceToIssue(null);
        }
    };

    const issueInvoiceHandler = useCallback(() => {
        if (!invoiceToIssue || pendingIssueRef.current) {
            return;
        }

        pendingIssueRef.current = true;
        dispatch(issueInvoiceThunk(apiClientWrapper, invoiceToIssue.id));
    }, [apiClientWrapper, dispatch, invoiceToIssue]);

    const openEditInvoiceDateHandler = () => {
        if (invoiceListRecord) {
            setInvoiceToEditDate(invoiceListRecord);
        }
    };

    const closeEditInvoiceDateHandler = () => {
        if (!savingInvoiceDate) {
            setInvoiceToEditDate(null);
        }
    };

    const saveInvoiceDateHandler = useCallback(
        (date: Moment) => {
            if (!invoiceToEditDate) {
                return;
            }

            pendingDateSaveRef.current = true;
            dispatch(saveDateThunk(apiClientWrapper, invoiceToEditDate.id, date.format('YYYY-MM-DD HH:mm:ss')));
        },
        [apiClientWrapper, dispatch, invoiceToEditDate]
    );

    const drawerZIndex = zIndex ?? defaultDrawerZIndex;
    const modalZIndex = drawerZIndex + 100;
    const toolbarZIndex = drawerZIndex + 1;

    return (
        <>
            <Drawer
                zIndex={zIndex}
                wrapperClassName={styles.drawerWrapper}
                onClose={onClose}
            >
                <div className={styles.drawerLayout}>
                    <div className={styles.tabsWrapper}>
                        <TabsHeader
                            className={styles.tabs}
                            tabs={tabs}
                            selectedTabId={selectedTabId}
                            tabHeaderClasses={{
                                container: styles.tabHeaderContainer,
                                label: styles.tabHeaderLabel,
                                selectedContainer: styles.tabHeaderContainerSelected
                            }}
                            onClick={selectTabHandler}
                        />
                    </div>

                    <DrawerBody className={clsx(styles.body, className)}>
                        {showReceipt ? (
                            <Receipt
                                payment={showReceipt}
                                withClose
                                refundModalZIndex={modalZIndex}
                                onBack={() => setShowReceipt(undefined)}
                                onRefunded={refundedHandler}
                            />
                        ) : (
                            <>
                                {selectedTabId === detailsTabId && (
                                    <>
                                        {isLoading && (
                                            <div className={styles.loadingContainer}>
                                                <Spinner />
                                            </div>
                                        )}
                                        {!isLoading && invoice && (
                                            <InvoiceDetailContent
                                                invoice={invoice}
                                                marketplaceId={marketplaceId}
                                                marketplaceName={marketplaceName}
                                                toolbarZIndex={toolbarZIndex}
                                                isCheckoutDisabled={isCheckoutDisabled}
                                                onCheckout={startCheckoutHandler}
                                                onSendInvoice={openSendInvoiceHandler}
                                                onVoidInvoice={openVoidInvoiceHandler}
                                                onDeleteInvoice={openDeleteInvoiceHandler}
                                                onEditDate={openEditInvoiceDateHandler}
                                                onIssueInvoice={openIssueInvoiceHandler}
                                            />
                                        )}
                                    </>
                                )}

                                {selectedTabId === transactionsTabId && (
                                    <div className={styles.transactionsTab}>
                                        <Transactions
                                            invoiceId={invoiceId}
                                            onPay={() => setSelectedTabId(detailsTabId)}
                                            onViewPayment={payment => setShowReceipt(payment)}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </DrawerBody>

                    {!isLoading && invoice && selectedTabId === detailsTabId && !showReceipt && (
                        <DrawerFooter
                            sticky={false}
                            className={styles.footer}
                        >
                            <Button
                                label="View PDF"
                                variant="outlined"
                                size="large"
                                fullWidth
                                disabled={!invoice.pdfUrl}
                                isLoading={updatingPdf}
                                onClick={viewPdfHandler}
                            />
                            {showCheckoutOption && (
                                <Button
                                    label="Checkout"
                                    variant="black"
                                    size="large"
                                    fullWidth
                                    disabled={isCheckoutDisabled}
                                    onClick={startCheckoutHandler}
                                />
                            )}
                        </DrawerFooter>
                    )}
                </div>
            </Drawer>

            <SendInvoiceModal
                invoice={invoiceToSend}
                sending={sendingInvoice}
                zIndex={modalZIndex}
                onClose={closeSendInvoiceHandler}
                onSend={sendInvoiceHandler}
            />

            <EditInvoiceDateModal
                invoice={invoiceToEditDate}
                saving={savingInvoiceDate}
                zIndex={modalZIndex}
                onClose={closeEditInvoiceDateHandler}
                onSave={saveInvoiceDateHandler}
            />

            <VoidInvoiceModal
                invoice={invoiceToVoid}
                voiding={voidingInvoice}
                zIndex={modalZIndex}
                onClose={closeVoidInvoiceHandler}
                onConfirm={voidInvoiceHandler}
            />

            <DeleteInvoiceModal
                invoice={invoiceToDelete}
                deleting={deletingInvoice}
                zIndex={modalZIndex}
                onClose={closeDeleteInvoiceHandler}
                onConfirm={deleteInvoiceHandler}
            />

            <IssueInvoiceModal
                invoice={invoiceToIssue}
                issuing={issuingInvoice}
                zIndex={modalZIndex}
                onClose={closeIssueInvoiceHandler}
                onConfirm={issueInvoiceHandler}
            />
        </>
    );
};

export default InvoiceDetail;
